import {
  InternalApiCustomListRecord,
  InternalApiCustomListSchemaField,
  InternalApiCustomListSchemaFieldType,
} from '../../api-internal';
import { RecordGroup } from './CustomListRecordPage';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

const getValueFromRecord = (
  record: InternalApiCustomListRecord,
  parentFieldName: string,
  childFieldName?: string
): string | null => {
  if (parentFieldName === 'id') return record.id;

  if (childFieldName) {
    return (record.attributes[parentFieldName] as Record<string, string>)?.[childFieldName] ?? null;
  }

  return (record.attributes[parentFieldName] ?? null) as unknown as string | null;
};

const getMultiValueFromRecord = (record: InternalApiCustomListRecord, parentFieldName: string): any[] => {
  const value = record.attributes[parentFieldName];
  return Array.isArray(value) ? value : [];
};

export const initInitialValues = (
  schemaFields?: InternalApiCustomListSchemaField[],
  record?: InternalApiCustomListRecord
) => {
  const singleNonObjectElements =
    schemaFields
      ?.filter((schemaField) => !schemaField.parentId && !schemaField.isMulti)
      .filter((noParentField) => !schemaFields.some((field) => field.parentId === noParentField.id)) || [];

  // Add external ID field as well
  singleNonObjectElements.unshift({
    id: uuidv4(),
    displayName: 'External ID (optional)',
    fieldName: 'id',
    isMulti: false,
    type: InternalApiCustomListSchemaFieldType.STRING,
    visibleInList: false,
    visibleInMatches: false,
    isNullable: true,
    sortOrder: 0,
  });

  const multiNonObjectElements = schemaFields
    ?.filter((schemaField) => !schemaField.parentId && schemaField.isMulti)
    .filter((noParentField) => !schemaFields.some((field) => field.parentId === noParentField.id));

  const singleObjectElements = schemaFields
    ?.filter((schemaField) => !schemaField.parentId && !schemaField.isMulti)
    .filter((noParentField) => schemaFields.some((field) => field.parentId === noParentField.id));

  const multiObjectElements = schemaFields
    ?.filter((schemaField) => !schemaField.parentId && schemaField.isMulti)
    .filter((noParentField) => schemaFields.some((field) => field.parentId === noParentField.id));

  return {
    ...singleNonObjectElements?.reduce(
      (current: { [key: string]: string | null }, element: InternalApiCustomListSchemaField) => {
        current[element.fieldName] = record
          ? getValueFromRecord(record, element.fieldName)
          : element.isNullable
            ? null
            : '';
        return current;
      },
      {}
    ),
    ...singleObjectElements
      ?.map((element) => {
        return {
          name: element.fieldName,
          id: element.id,
        };
      })
      .reduce((current: { [key: string]: { [key: string]: string | null } }, item) => {
        current[item.name] = {
          ...schemaFields
            ?.filter((schemaField) => schemaField.parentId === item.id)
            .reduce((current1: { [key: string]: string | null }, item1) => {
              current1[item1.fieldName] = record
                ? getValueFromRecord(record, item.name, item1.fieldName)
                : item1.isNullable
                  ? null
                  : '';
              return current1;
            }, {}),
        };
        return current;
      }, {}),
    ...multiNonObjectElements
      ?.map((element) => element.fieldName)
      .reduce((current: { [key: string]: string[] }, item) => {
        current[item] = record ? getMultiValueFromRecord(record, item) : [];
        return current;
      }, {}),
    ...multiObjectElements
      ?.map((element) => {
        return {
          name: element.fieldName,
          id: element.id,
        };
      })
      .reduce((current: { [key: string]: { [key: string]: string }[] }, item) => {
        current[item.name] = record ? getMultiValueFromRecord(record, item.name) : [];
        return current;
      }, {}),
  };
};

export const groupFields = (schemaFields: InternalApiCustomListSchemaField[]) => {
  const externalIdField = {
    displayName: 'External ID (optional)',
    fieldName: 'id',
    isMulti: false,
    type: InternalApiCustomListSchemaFieldType.STRING,
    isNullable: true,
  } as InternalApiCustomListSchemaField;
  const groups: RecordGroup[] = [
    {
      key: {
        type: externalIdField.type,
        isObject: false,
        isMulti: externalIdField.isMulti,
      },
      items: [externalIdField],
    },
  ];
  const nonChildFields = schemaFields.filter((s) => !s.parentId);
  for (const schemaField of nonChildFields) {
    const previousKey = groups[groups.length - 1].key;
    const currentKey = {
      isMulti: schemaField.isMulti,
      isObject: schemaField.type === InternalApiCustomListSchemaFieldType.OBJECT,
      type: schemaField.type,
    };
    if (
      !previousKey.isObject &&
      !currentKey.isObject &&
      (!previousKey.isMulti || previousKey.type === InternalApiCustomListSchemaFieldType.ENUM) &&
      (!currentKey.isMulti || currentKey.type === InternalApiCustomListSchemaFieldType.ENUM)
    ) {
      const el = groups[groups.length - 1];
      el.items.push(schemaField);
      groups[groups.length - 1] = el;
    } else {
      groups.push({ key: currentKey, items: [schemaField] });
    }
  }
  return groups;
};

export const getValidationSchema = (schemaFields: InternalApiCustomListSchemaField[], parentId?: string) => {
  const yupSchema: Record<string, Yup.AnySchema> = {};

  schemaFields
    .filter((schemaField) => (parentId ? schemaField.parentId === parentId : !schemaField.parentId))
    .forEach((schemaField) => {
      let yupField: Yup.AnySchema;
      if (schemaField.type === InternalApiCustomListSchemaFieldType.OBJECT && schemaField.isMulti) {
        yupField = Yup.array().of(getValidationSchema(schemaFields, schemaField.id));
      } else if (schemaField.type === InternalApiCustomListSchemaFieldType.OBJECT) {
        yupField = getValidationSchema(schemaFields, schemaField.id);
      } else if (schemaField.isMulti) {
        yupField = Yup.array().of(Yup.string());
      } else {
        yupField = Yup.string();
      }

      if (!schemaField.isNullable) {
        yupField = yupField.required('This field is required');

        if (schemaField.isMulti) {
          if (schemaField.type !== InternalApiCustomListSchemaFieldType.OBJECT) {
            yupField = Yup.array().of(Yup.string().required());
          }

          yupField = (yupField as Yup.ArraySchema<any[], any>).min(1);
        }
      }

      if (schemaField.isNullable) {
        yupField = yupField.nullable();
      }

      yupSchema[schemaField.fieldName] = yupField;
    });

  return Yup.object().shape(yupSchema);
};

export interface NonConformingEntry {
  fieldName: string;
  label: string;
  value: unknown;
  excludeFromSchema: boolean;
}

export interface NonConformingResult {
  nonConformingEntries: NonConformingEntry[];
  nonConformingFieldNames: Set<string>;
}

const getChildFieldNames = (schemaFields: InternalApiCustomListSchemaField[], parentId: string): Set<string> =>
  new Set(schemaFields.filter((f) => f.parentId === parentId).map((f) => f.fieldName));

const isTypeMismatch = (hasChildren: boolean, isMulti: boolean, value: unknown): boolean => {
  if (hasChildren && !isMulti) {
    return typeof value !== 'object' || Array.isArray(value);
  }
  if (isMulti) {
    return !Array.isArray(value);
  }
  return false;
};

const findExtraChildEntries = (
  fieldName: string,
  displayName: string,
  value: unknown,
  childFieldNames: Set<string>,
  isMulti: boolean
): NonConformingEntry[] => {
  const collectExtraKeys = (obj: Record<string, unknown>, labelPrefix: string) =>
    Object.entries(obj)
      .filter(([childKey]) => !childFieldNames.has(childKey))
      .map(([childKey, childValue]) => ({
        fieldName,
        label: `${labelPrefix} > ${childKey}`,
        value: childValue,
        excludeFromSchema: false,
      }));

  if (!isMulti) {
    return collectExtraKeys(value as Record<string, unknown>, displayName);
  }
  return (value as Record<string, unknown>[]).flatMap((item, index) =>
    typeof item === 'object' && item !== null
      ? collectExtraKeys(item as Record<string, unknown>, `${displayName}[${index}]`)
      : []
  );
};

export const findNonConformingAttributes = (
  schemaFields: InternalApiCustomListSchemaField[],
  record?: InternalApiCustomListRecord
): NonConformingResult => {
  if (!record) {
    return { nonConformingEntries: [], nonConformingFieldNames: new Set() };
  }

  const topLevelFields = schemaFields.filter((field) => !field.parentId);
  const entries: NonConformingEntry[] = [];

  for (const [key, value] of Object.entries(record.attributes)) {
    const schemaField = topLevelFields.find((f) => f.fieldName === key);

    if (!schemaField) {
      entries.push({ fieldName: key, label: key, value, excludeFromSchema: true });
      continue;
    }

    if (value === null || value === undefined) {
      continue;
    }

    const hasChildren = schemaFields.some((f) => f.parentId === schemaField.id);

    if (isTypeMismatch(hasChildren, schemaField.isMulti, value)) {
      entries.push({ fieldName: key, label: schemaField.displayName, value, excludeFromSchema: true });
      continue;
    }

    if (hasChildren) {
      const childFieldNames = getChildFieldNames(schemaFields, schemaField.id);
      entries.push(...findExtraChildEntries(key, schemaField.displayName, value, childFieldNames, schemaField.isMulti));
    }
  }

  const nonConformingFieldNames = new Set(entries.filter((e) => e.excludeFromSchema).map((e) => e.fieldName));

  return { nonConformingEntries: entries, nonConformingFieldNames };
};
